import { getCsrfToken } from './helpers.js';

const csrfFailureMessages = [
    'Unable to verify your data submission',
    'The CSRF token could not be verified'
];
const csrfRetryHeader = 'X-Avari-CSRF-Retry';

function htmxForm(event) {
    const source = event?.detail?.elt;
    const target = event?.detail?.target;

    return source?.matches?.('form')
        ? source
        : source?.closest?.('form') || target?.querySelector?.('form');
}

export function isCsrfFailure(status, responseText = '') {
    if (Number(status) !== 400 || typeof responseText !== 'string') {
        return false;
    }

    return csrfFailureMessages.some((message) =>
        responseText.includes(message)
    );
}

export async function isCsrfFailureResponse(response) {
    if (!response || Number(response.status) !== 400) {
        return false;
    }

    try {
        return isCsrfFailure(response.status, await response.clone().text());
    } catch {
        return false;
    }
}

/**
 * Send a protected JSON request with one bounded retry after Craft explicitly
 * rejects CSRF validation. The retry is safe because CSRF validation happens
 * before the controller action runs.
 */
export async function fetchJsonWithCsrf(
    url,
    payload = {},
    {
        forceRefresh = false,
        retryOnCsrf = true,
        headers = {},
        ...requestOptions
    } = {}
) {
    const send = async (refreshToken, isRetry = false) => {
        const csrfToken = await getCsrfToken(refreshToken);
        if (!csrfToken?.name || !csrfToken?.value) {
            throw new Error('CSRF token not available');
        }

        return fetch(url, {
            method: 'POST',
            credentials: 'same-origin',
            ...requestOptions,
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                'X-Avari-Request-Surface': 'booking',
                ...headers,
                ...(isRetry ? { [csrfRetryHeader]: '1' } : {}),
                'X-CSRF-Token': csrfToken.value
            },
            body: JSON.stringify({
                ...payload,
                [csrfToken.name]: csrfToken.value
            })
        });
    };

    const response = await send(forceRefresh);
    if (!retryOnCsrf || !(await isCsrfFailureResponse(response))) {
        return response;
    }

    return send(true, true);
}

export function markHtmxCsrfRetryRequest(event, retryingForms) {
    const form = htmxForm(event);
    if (!form || !retryingForms.has(form)) {
        return false;
    }

    event.detail.headers = event.detail.headers || {};
    event.detail.headers[csrfRetryHeader] = '1';
    retryingForms.delete(form);

    return true;
}

export async function retryHtmxCsrfFailure(
    event,
    retriedForms,
    retryingForms = null
) {
    const xhr = event?.detail?.xhr;
    if (!xhr || !isCsrfFailure(xhr.status, xhr.responseText)) {
        return false;
    }

    const form = htmxForm(event);

    if (!form || retriedForms.has(form)) {
        return false;
    }

    retriedForms.add(form);
    try {
        const csrfToken = await getCsrfToken(true);
        if (!csrfToken?.name || !csrfToken?.value) {
            retriedForms.delete(form);
            return false;
        }

        const csrfInput = Array.from(form.elements || []).find(
            (element) => element?.name === csrfToken.name
        );
        if (!csrfInput || !window.htmx) {
            retriedForms.delete(form);
            return false;
        }

        csrfInput.value = csrfToken.value;
        retryingForms?.add(form);
        window.htmx.trigger(form, 'submit');
        return true;
    } catch {
        retryingForms?.delete(form);
        retriedForms.delete(form);
        return false;
    }
}
